import { Log } from '@ainova/core';
import { createReportingService } from '$lib/reporting';
import { ApiError } from '$lib/api/client';

const logger = new Log('ErrorHandler');

export interface ErrorContext {
  operation?: string;
  component?: string;
  userId?: string;
  metadata?: Record<string, unknown>;
}

export class AppError extends Error {
  constructor(
    message: string,
    public code: string,
    public context?: ErrorContext
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export async function handleError(error: unknown, context?: ErrorContext): Promise<string> {
  let errorMessage: string;
  let errorDetails: Record<string, unknown> = {};

  // Parse error type
  if (error instanceof ApiError) {
    errorMessage = `API Error: ${error.message}`;
    errorDetails = {
      status: error.status,
      data: error.data,
      type: 'api_error'
    };
  } else if (error instanceof AppError) {
    errorMessage = error.message;
    errorDetails = {
      code: error.code,
      context: error.context,
      type: 'app_error'
    };
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorDetails = {
      name: error.name,
      stack: error.stack,
      type: 'generic_error'
    };
  } else {
    errorMessage = 'An unexpected error occurred';
    errorDetails = {
      error: String(error),
      type: 'unknown_error'
    };
  }

  // Log error
  logger.error(errorMessage, {
    ...errorDetails,
    context
  });

  // Report to error tracking service
  try {
    const reporting = createReportingService();
    await reporting.error(errorMessage, {
      ...errorDetails,
      context
    });
  } catch (reportingError) {
    logger.error('Failed to report error', reportingError);
  }

  // Return user-friendly message
  return getUserFriendlyMessage(error);
}

function getUserFriendlyMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'You are not authorized. Please log in and try again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 408:
        return 'Request timeout. Please check your connection and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
      case 502:
      case 503:
        return 'Server error. Please try again later.';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  if (error instanceof AppError) {
    // App errors should have user-friendly messages
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
}

export function createErrorHandler(context: ErrorContext) {
  return (error: unknown) => handleError(error, context);
}

// Validation error helper
export class ValidationError extends AppError {
  /**
   * The form field that failed.
   *
   * Also carried in `context.metadata` for the reporter. It is a property in
   * its own right because the order form indexes its error map by it — and
   * was reading `err.field`, which did not exist, so every validation message
   * landed under `undefined` instead of beside the field it belongs to.
   */
  readonly field: string;

  constructor(field: string, message: string, value?: unknown) {
    super(`Validation failed for ${field}: ${message}`, 'VALIDATION_ERROR', {
      metadata: { field, value }
    });
    this.name = 'ValidationError';
    this.field = field;
  }
}

// Network error helper
export class NetworkError extends AppError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, 'NETWORK_ERROR', {
      metadata: details
    });
  }
}

// Business logic error helper
export class BusinessError extends AppError {
  constructor(message: string, code: string, details?: Record<string, unknown>) {
    super(message, code, {
      metadata: details
    });
  }
}
